import { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, X, DownloadCloud, AlertTriangle, Lock, Check, Power, Clock } from 'lucide-react';
import { filesAPI, externalSubsAutoAPI } from '../api';
import './BulkEditor.css';
import './RefreshAllExternalSubs.css';

const fmtTime = (iso) => iso ? new Date(iso).toLocaleString('ru-RU', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' }) : '—';

/**
 * Массовое обновление файлов с привязанными внешними подписками.
 *
 * Делает то же, что кнопка «Обновить» в панели одного файла (ExternalSubsPanel), но по всем
 * файлам сразу: вписывает свежую выдачу источников на место прошлой. Ссылки, вписанные
 * руками (наши VPS-входы), не трогаются. Серверной логики под это НЕ заводили: роут
 * POST /files/:name/external-subs/refresh уже умеет всё нужное для одного файла, включая
 * главную защиту — если источник не ответил, файл не трогается вовсе (он отвечает
 * HTTP 200 с success:false, а не ошибкой). Поэтому здесь просто цикл по существующему роуту.
 *
 * Почему цикл на клиенте, а не bulk-роут на сервере: источников столько же, сколько файлов
 * (у каждого файла свой токен, общий кэш их не схлопывает), таймаут одного источника 12с —
 * то есть один серверный запрос мог бы идти минуты, дольше дефолтного таймаута axios, и без
 * всякого прогресса. Здесь же виден каждый файл по мере обработки.
 */
function RefreshAllExternalSubs({ files = [], onClose, onRefreshed }) {
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState([]);
  const [message, setMessage] = useState('');

  const [autoStatus, setAutoStatus] = useState(null);
  const [autoLogs, setAutoLogs] = useState([]);
  const [autoBusy, setAutoBusy] = useState(false);
  const pollRef = useRef(null);

  const withSubs = files.filter((f) => (f.externalSubsCount || 0) > 0);
  // Замок означает «файл руками не трогать» — так же его понимает POST /bulk/update-all.
  const locked = withSubs.filter((f) => f.locked);
  const targets = withSubs.filter((f) => !f.locked);

  const shortName = (n) => String(n).replace(/\.json$/, '');

  const loadAutoState = useCallback(async () => {
    try {
      const [statusRes, logsRes] = await Promise.all([
        externalSubsAutoAPI.getStatus(),
        externalSubsAutoAPI.getLogs({ limit: 5 }),
      ]);
      setAutoStatus(statusRes.data);
      setAutoLogs(logsRes.data || []);
    } catch {
      // панель открыта без прав/оффлайн — просто не показываем блок автотаска
    }
  }, []);

  useEffect(() => {
    loadAutoState();
    pollRef.current = setInterval(loadAutoState, 15000);
    return () => clearInterval(pollRef.current);
  }, [loadAutoState]);

  const toggleAuto = async () => {
    setAutoBusy(true);
    try {
      if (autoStatus?.enabled) await externalSubsAutoAPI.stop();
      else await externalSubsAutoAPI.start();
      await loadAutoState();
    } catch (error) {
      setMessage(`⚠️ Не удалось переключить автотаск: ${error.response?.data?.error || error.message}`);
    } finally {
      setAutoBusy(false);
    }
  };

  const run = async () => {
    if (!targets.length) return;

    setIsRunning(true);
    setResults([]);
    setMessage('');
    setProgress({ current: 0, total: targets.length });

    const collected = [];
    for (let i = 0; i < targets.length; i++) {
      const file = targets[i];
      try {
        const { data } = await filesAPI.refreshExternalSubs(file.name);
        collected.push(
          data.success
            ? { name: file.name, ok: true, own: data.own, external: data.external,
                total: data.total, removed: data.removed, sources: data.sources }
            // Источник не ответил. Файл на сервере остался прежним — это штатный исход,
            // а не сбой прогона, поэтому идём дальше к следующему файлу.
            : { name: file.name, ok: false, error: data.error }
        );
      } catch (error) {
        const msg = error.response?.data?.error || error.message;
        collected.push({ name: file.name, ok: false, error: msg });
        if (error.response?.status === 401) {
          setResults([...collected]);
          setMessage(`Сессия истекла после ${collected.length} файл(ов).`);
          setTimeout(() => window.location.reload(), 2000);
          return;
        }
      }

      setResults([...collected]);
      setProgress((p) => ({ ...p, current: p.current + 1 }));

      // Пауза между файлами — намеренно длиннее, чем 300мс в BulkEditor. Источник у каждого
      // файла свой, то есть одно нажатие дёргает столько же разных токенов панели, сколько
      // файлов. Панель уже убивала токены, заметив всплеск обращений (журнал 2026-08-20),
      // поэтому запросы идут строго по одному и вразбивку, а не Promise.all.
      if (i < targets.length - 1) await new Promise((r) => setTimeout(r, 1000));
    }

    const okCount = collected.filter((r) => r.ok).length;
    const badCount = collected.length - okCount;
    setMessage(
      (badCount ? '⚠️' : '✅') +
      ` Обновлено ${okCount} из ${targets.length}` +
      (badCount ? `, ошибок ${badCount}` : '') +
      (locked.length ? `, пропущено под замком ${locked.length}` : '')
    );

    setIsRunning(false);
    onRefreshed?.();
  };

  return (
    <div className="bulk-editor-overlay">
      <div className="bulk-editor-modal">
        <div className="bulk-editor-header">
          <h2><RefreshCw size={16} /> Обновить внешние подписки</h2>
          <button onClick={onClose} className="close-btn" disabled={isRunning}><X size={15} /></button>
        </div>

        <div className="bulk-editor-info">
          <p>
            Файлов с привязанной подпиской: <strong>{targets.length}</strong> из {files.length}.
          </p>
          {locked.length > 0 && (
            <p className="warning-text" style={{ color: 'var(--text-muted)' }}>
              <Lock size={13} /> Пропущено {locked.length} под замком: {locked.map((f) => shortName(f.name)).join(', ')}
            </p>
          )}
          <p className="warning-text" style={{ color: 'var(--text-muted)' }}>
            В файлах обновится только часть, пришедшая с источников. Ссылки, вписанные руками
            (наши VPS-входы), останутся на месте.
          </p>

          {isRunning && progress.total > 0 && (
            <div className="progress-section">
              <div className="progress-label">Обновлено {progress.current} из {progress.total}...</div>
              <div className="progress-bar-track">
                <div className="progress-bar-fill" style={{ width: `${(progress.current / progress.total) * 100}%` }} />
              </div>
            </div>
          )}

          {message && (
            <div className={`message ${message.startsWith('✅') ? 'success' : 'error'}`}>{message}</div>
          )}

          {autoStatus && (
            <div className="auto-refresh-section">
              <div className="auto-refresh-row">
                <button
                  onClick={toggleAuto}
                  disabled={autoBusy}
                  className={`btn btn-sm ${autoStatus.enabled ? 'btn-success' : 'btn-secondary'}`}
                  title={autoStatus.enabled ? 'Выключить авто-обновление' : 'Включить авто-обновление'}
                >
                  <Power size={13} /> Авто каждые {autoStatus.intervalMinutes} мин: {autoStatus.enabled ? 'включено' : 'выключено'}
                </button>
                <span className="auto-refresh-meta">
                  <Clock size={12} /> последний проход: {fmtTime(autoStatus.lastRunAt)}
                  {autoStatus.running ? ' · идёт сейчас' : ''}
                </span>
              </div>
              {autoLogs.length > 0 && (
                <ul className="auto-refresh-log">
                  {autoLogs.slice().reverse().map((e) => (
                    <li key={e.id} className={`auto-refresh-log-row level-${e.level}`}>
                      <span className="auto-refresh-log-time">{fmtTime(e.timestamp)}</span>
                      <span className="auto-refresh-log-msg">{e.message}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* refresh-all-content vozvrashchaet vertikalnyy skroll: bazovyy
            .bulk-editor-content sdelan pod tekstovoe pole vo ves' rost
            (display:flex + overflow:hidden), i spisok iz 13 strok v nem obrezalsya. */}
        <div className="bulk-editor-content refresh-all-content">
          {results.length === 0 ? (
            <div className="refresh-all-empty">
              {targets.length
                ? 'Нажми «Обновить все» — файлы пойдут по одному, результат появится здесь.'
                : 'Ни к одному файлу не привязана внешняя подписка.'}
            </div>
          ) : (
            <ul className="refresh-all-list">
              {results.map((r) => (
                <li key={r.name} className={`refresh-all-row ${r.ok ? 'good' : 'bad'}`}>
                  <span className="refresh-all-icon">
                    {r.ok ? <Check size={13} /> : <AlertTriangle size={13} />}
                  </span>
                  <span className="refresh-all-name">{shortName(r.name)}</span>
                  <span className="refresh-all-detail">
                    {r.ok ? (
                      <>
                        в файле {r.total}: наших {r.own} + {r.external}
                        {r.removed ? <span className="refresh-all-del"> ↻{r.removed} чужих</span> : null}
                        {r.sources > 1 ? `, источников ${r.sources}` : ''}
                      </>
                    ) : (
                      r.error
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bulk-editor-footer">
          <span className="file-count">С подпиской: {targets.length} / {files.length}</span>
          <div className="bulk-actions">
            <button onClick={onClose} className="btn btn-secondary" disabled={isRunning}>Закрыть</button>
            <button onClick={run} className="btn btn-success" disabled={isRunning || !targets.length}>
              <DownloadCloud size={14} /> {isRunning ? 'Обновляю...' : 'Обновить все'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RefreshAllExternalSubs;
